import React, { useMemo, useState } from 'react'

import { Button } from '@cmsgov/ds-medicare-gov'
import { Button as CHButton } from 'components/Button/Button'

import { Modal } from '../../Modal/Modal'
import { MessagePane } from './MessagePane/MessagePane'
import { MessageInbox } from '../MessageInbox/MessageInbox'
import { useSelector, useDispatch } from 'react-redux'
import { getAllMessages, getSelectedMessageId, markMessagesAsRead, setSelectedMessageId, clearSelectedMessageId } from '../../../redux/reducers/messages'
import { getUserLoggedInState } from '../../../redux/reducers/users'
import { useTranslation } from 'react-i18next'
// Tealium
import { Categories } from 'constants/analytics'

import events, { Event } from 'pubsub/events'

export interface MessageCenterModalProps {
  onClose: () => void
}

export const MessageCenterModal: React.FC<MessageCenterModalProps> = ({
  onClose
}) => {
  const STARTING_MESSAGE_COUNT = 5
  const NUM_ADDITIONAL_MESSAGES_TO_SHOW = 5

  const dispatch = useDispatch()
  const messages = useSelector(getAllMessages)

  const unreadMessages = messages.filter(message => !message.read)
  const shouldIncreaseStartingMessageCount = STARTING_MESSAGE_COUNT <= unreadMessages.length
  const newStartingMessageCount = shouldIncreaseStartingMessageCount ? unreadMessages.length + 1 : STARTING_MESSAGE_COUNT

  const [showMessageCount, setShowMessageCount] = useState<number>(newStartingMessageCount)
  const isLoggedIn = useSelector(getUserLoggedInState)
  const selectedModalMessageId = useSelector(getSelectedMessageId)
  const { t, i18n } = useTranslation('messageCenter')

  const hasMoreMessages = showMessageCount < messages.length

  // Derived States
  const messagePayload = useMemo(() => messages.find(x => x.guid + '' === selectedModalMessageId), [selectedModalMessageId, messages])
  const headingContent = useMemo(() => {
    if (selectedModalMessageId) {
      return messages.find(x => x.guid + '' === selectedModalMessageId)?.subject || (t('My Messages'))
    } else {
      return (t('My Messages'))
    }
  }, [selectedModalMessageId, messages, t])
  const closeButtonRef = document.querySelectorAll('.ch-c-dialog__cross')[0] as HTMLElement

  // TODO: This should be `useRef`?
  const contentContainerRef = React.createRef<HTMLElement>()
  const scrollToTopOfContentContainer = () => {
    if (contentContainerRef.current && contentContainerRef.current.scrollTo) {
      contentContainerRef?.current?.scrollTo(0, 0)
    }
  }

  // Functions
  const onBackMessageClick = () => {
    // tealium
    const index = messages.findIndex(x => x.guid + '' === selectedModalMessageId)
    const messageSubject = messages[index].subject

    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: t('Previous') + `: ${messageSubject}`,
      category: Categories.Messages,
      element: 'button',
      messageSubject: messages[index].subject,
      messageStatus: '',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: true
    }
    events.publish(clickEvent)

    const foundIndex = messages.findIndex(x => x.guid + '' === selectedModalMessageId)
    let newIndex
    if (foundIndex === 0) {
      newIndex = messages.length - 1
    } else {
      newIndex = foundIndex - 1
    }

    const prevMessage = messages[newIndex]
    if (!prevMessage.read) {
      dispatch(markMessagesAsRead({ messageIds: [prevMessage.guid], selectedMessageId: prevMessage.guid }))
    } else {
      dispatch(setSelectedMessageId(prevMessage.guid))
    }

    const clickEventReadOrOpenMessage: Event = {
      authenticated: isLoggedIn,
      text: prevMessage.subject,
      category: Categories.Messages,
      element: 'link',
      messageSubject: prevMessage.subject,
      messageStatus: prevMessage.read === false ? 'unread' : 'read',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: prevMessage.read === false ? 'Message read' : 'Message open',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: false,
      sendToCoreAPI: true,
      isMessageCenter: true,
      messageId: prevMessage.guid,
      profileId: prevMessage.profileId
    }
    events.publish(clickEventReadOrOpenMessage)

    scrollToTopOfContentContainer()
  }

  const onNextMessageClick = () => {
    // tealium
    const index = messages.findIndex(x => x.guid + '' === selectedModalMessageId)
    const messageSubject = messages[index].subject

    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: t('Next') + `: ${messageSubject}`,
      category: Categories.Messages,
      element: 'button',
      messageSubject: messageSubject,
      messageStatus: '',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: true
    }
    events.publish(clickEvent)

    const foundIndex = messages.findIndex(x => x.guid + '' === selectedModalMessageId)
    let newIndex
    if (foundIndex === messages.length - 1) {
      newIndex = 0
    } else {
      newIndex = foundIndex + 1
    }

    const nextMessage = messages[newIndex]
    if (!nextMessage.read) {
      dispatch(markMessagesAsRead({ messageIds: [nextMessage.guid], selectedMessageId: nextMessage.guid }))
    } else {
      dispatch(setSelectedMessageId(nextMessage.guid))
    }

    const clickEventReadOrOpenMessage: Event = {
      authenticated: isLoggedIn,
      text: nextMessage.subject,
      category: Categories.Messages,
      element: 'link',
      messageSubject: nextMessage.subject,
      messageStatus: nextMessage.read === false ? 'unread' : 'read',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: nextMessage.read === false ? 'Message read' : 'Message open',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: false,
      sendToCoreAPI: true,
      isMessageCenter: true,
      messageId: nextMessage.guid,
      profileId: nextMessage.profileId
    }
    events.publish(clickEventReadOrOpenMessage)

    scrollToTopOfContentContainer()
  }

  const onViewAllMessagesClick = () => {
    // tealium
    const index = messages.findIndex(x => x.guid + '' === selectedModalMessageId)
    const messageSubject = messages[index].subject

    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: t('View all messages') + `: ${messageSubject}`,
      category: Categories.Messages,
      element: 'link',
      messageSubject: messageSubject,
      messageStatus: '',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: true
    }
    events.publish(clickEvent)

    dispatch(clearSelectedMessageId())
    if (closeButtonRef) {
      closeButtonRef.focus()
    }
  }

  const onModalViewMoreMessagesClick = () => {
    // tealium

    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: t('View more messages'),
      category: Categories.Messages,
      element: 'button',
      messageSubject: '',
      messageStatus: '',
      eventName: 'message_center_my_messages_click',
      messageComponent: 'my messages',
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: true
    }
    events.publish(clickEvent)

    setShowMessageCount(showMessageCount + NUM_ADDITIONAL_MESSAGES_TO_SHOW)
  }
  return (
    <Modal
      heading={headingContent}
      onClose={onClose}
      testid='message-center-modal'
      contentContainerRef={contentContainerRef}
      className='ch-c-dialog__message-center'
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className='ch-c-message-modal-content'>
        <MessageInbox
          isInsideOfModal
          selectedMessageId={selectedModalMessageId}
          showMessageCount={showMessageCount}
          className={`ch-c-message-inbox-modal ${selectedModalMessageId ? 'ch-u-display--none' : ''}`}
        />
        {selectedModalMessageId &&
          <MessagePane
            messagePayload={messagePayload}
          />}
        {!selectedModalMessageId && hasMoreMessages &&
          <footer className='ch-c-dialog__footer ch-c-dialog__footer__all-messages-pane'>
            <Button
              data-testid='view-more-messages-modal-btn'
              onClick={onModalViewMoreMessagesClick}
              className='ch-c-button--primary'
            >
              {t('View more messages')}
            </Button>
          </footer>}
      </div>
      {selectedModalMessageId &&
        <footer className='ch-c-dialog__footer ch-c-dialog__footer__preview-pane'>
          <CHButton data-testid='view-all' onClick={onViewAllMessagesClick} className='ds-c-button--ghost ch-u-remove--lg ch-u-ml--n3 ch-u-my--3 ch-c-btn__link'>
            {t('View all')}
          </CHButton>
          <CHButton data-testid='view-all-messages' onClick={onViewAllMessagesClick} className='ds-c-button--ghost ch-u-remove--md ch-u-ml--n3 ch-u-my--3 ch-c-btn__link'>
            {t('View all messages')}
          </CHButton>
          <div className='ch-c-dialog__footer_nextback'>
            <Button className='ch-u-z-index-focus' data-testid='prev-btn' onClick={onBackMessageClick}>{t('Previous')}</Button>
            <Button className='ch-u-z-index-focus' data-testid='next-btn' onClick={onNextMessageClick}>{t('Next')}</Button>
          </div>
        </footer>}

    </Modal>
  )
}
